import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { getIn } from 'formik'

import { concat } from '../../../../helpers/common'

import { MEDIA_FILE_OPTION, FILE_TYPE, FILE_URL, MEDIA_TYPE } from '../fields'
import { FILE_IS_ACTIVE } from '../../../../constants/ads'

export function useMediaUpload(props) {
  PropTypes.checkPropTypes(mediaPropTypes, props, 'prop', 'useMediaUpload')

  const { formik, itemPath, onMediaRemove, initialMediaFileValues, errorFieldName } = props
  const { values, setFieldValue, errors, touched } = formik

  const fileURLKey = concat(itemPath, '.', FILE_URL)
  const fileTypeKey = concat(itemPath, '.', FILE_TYPE)
  const mediaOptionKey = concat(itemPath, '.', MEDIA_FILE_OPTION)
  const uploadedMediaTypeKey = concat(itemPath, '.', MEDIA_TYPE)

  // select field based on which the main validation is applied to
  const errorField = errorFieldName || FILE_URL
  const mediaFile = getIn(values, itemPath)
  const mediaError = getIn(errors, `${itemPath}[${errorField}]`)
  const mediaTouched = getIn(touched, `${itemPath}[${errorField}]`)
  const selectedMediaOption = getIn(values, mediaOptionKey)
  const mediaMissingError = mediaTouched && mediaError

  const handleMediaRemove = useCallback(() => {
    // set initial media file values for deleted file
    setFieldValue(itemPath, initialMediaFileValues)
    onMediaRemove && onMediaRemove()
  }, [itemPath, setFieldValue, onMediaRemove, initialMediaFileValues])

  return useMemo(
    () => ({
      fileURLKey,
      fileTypeKey,
      mediaOptionKey,
      uploadedMediaTypeKey,
      mediaFile,
      mediaError,
      mediaTouched,
      selectedMediaOption,
      mediaMissingError,
      handleMediaRemove
    }),
    [
      fileURLKey,
      fileTypeKey,
      mediaOptionKey,
      uploadedMediaTypeKey,
      mediaFile,
      mediaError,
      mediaTouched,
      selectedMediaOption,
      mediaMissingError,
      handleMediaRemove
    ]
  )
}

const mediaPropTypes = {
  formik: PropTypes.object.isRequired,
  itemPath: PropTypes.string.isRequired,
  onMediaRemove: PropTypes.func,
  initialMediaFileValues: PropTypes.object.isRequired
}

// hook describes logic for video processing, more details in last useEffect
export function useVideoProcessing(props) {
  PropTypes.checkPropTypes(videoProcessingPropTypes, props, 'prop', 'useVideoProcessing')

  const { uploadedFileData, onFileUploaded } = props

  const [fileWasAccepted, setFileWasAccepted] = useState(false)
  const fileStatus = uploadedFileData && uploadedFileData.status
  const wasAccepted = useMemo(() => fileStatus === FILE_IS_ACTIVE, [fileStatus])

  useEffect(() => {
    if (wasAccepted) {
      setFileWasAccepted(true)
    }
  }, [wasAccepted])

  useEffect(() => {
    // after video file is uploaded to platform it starts processing and validation by platform
    // once file is accepted then all of its data is received, and we can set it to formik

    if (fileWasAccepted) {
      onFileUploaded(uploadedFileData)
      setFileWasAccepted(false)
    }
  }, [fileWasAccepted, uploadedFileData, onFileUploaded])
}

const videoProcessingPropTypes = {
  uploadedFileData: PropTypes.object,
  onFileUploaded: PropTypes.func,
  fileType: PropTypes.string
}
